import { useEffect, useMemo, useState } from 'react';
import { CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { coursesAPI, shopAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import {
  DataTable, EmptyPanel, FilterPills, PageHeader, PersonCell, SearchBox, StatCards, TableRow, Thumb,
  adminBtnOutline, adminCard, fmtDate, money,
} from '../../components/adminUi';

// Admin 5 · Payments (s134 redesign): period pills + search, stat cards with
// notes, Top courses by revenue card, payments table (member, course thumb,
// amount, platform / instructor split, date). Empty state lists what's on sale.
const COLS = [
  { label: 'Member', width: 'minmax(0,2fr)' },
  { label: 'Course', width: 'minmax(0,2.4fr)' },
  { label: 'Amount', width: '110px' },
  { label: 'Split · platform / instructor', width: '220px' },
  { label: 'Date', width: '120px' },
];

const sinceFor = (period) => {
  const d = new Date();
  if (period === '30d') d.setDate(d.getDate() - 30);
  else if (period === 'year') { d.setMonth(0, 1); d.setHours(0, 0, 0, 0); } else return '';
  return d.toISOString();
};

export default function PaymentsPage() {
  const [period, setPeriod] = useState('30d');
  const [report, setReport] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [onSale, setOnSale] = useState([]);
  const perPage = 25;
  const since = useMemo(() => sinceFor(period), [period]);

  useEffect(() => {
    setReport(null);
    shopAPI.revenueReport({ since }).then(({ data: d }) => setReport(d)).catch(() => {});
  }, [since]);
  useEffect(() => {
    shopAPI.adminPayments({ since, page, per_page: perPage })
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error(t('Could not load payments')));
  }, [since, page]);
  useEffect(() => {
    coursesAPI.list({ status: 'active', per_page: 100 })
      .then(({ data: d }) => setOnSale((d.courses || []).filter((c) => !c.is_free)))
      .catch(() => {});
  }, []);

  const rows = useMemo(() => {
    const list = (data && data.payments) || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => `${(p.member || {}).name || ''} ${(p.member || {}).email || ''} ${p.course_title || ''}`.toLowerCase().includes(q));
  }, [data, search]);

  const pages = data ? Math.max(1, Math.ceil(data.total / perPage)) : 1;
  const tot = (report && report.totals) || null;
  const periodLabel = { '30d': t('Last 30 days'), year: t('This year'), all: t('All time') }[period];

  return (
    <div className="flex flex-col gap-[22px]">
      <PageHeader
        title={t('Payments & revenue')}
        subtitle={t('Sales of paid courses and how each one is split between the platform and the instructor.')}
      />

      <FilterPills
        value={period}
        onChange={(v) => { setPeriod(v); setPage(1); }}
        items={[
          { key: '30d', label: t('Last 30 days') },
          { key: 'year', label: t('This year') },
          { key: 'all', label: t('All time') },
        ]}
      >
        <SearchBox value={search} onChange={setSearch} placeholder={t('Search member or course…')} />
      </FilterPills>

      {tot && (
        <StatCards
          items={[
            { label: t('Gross revenue'), value: money(tot.gross), note: `${tot.count} ${tot.count === 1 ? t('sale') : t('sales')}` },
            { label: t('Platform share'), value: money(tot.admin_revenue) },
            { label: t('Instructor share'), value: money(tot.instructor_revenue), note: t('owed via Payouts') },
            { label: t('Sales'), value: tot.count },
          ]}
        />
      )}

      <section className={`${adminCard} flex flex-col gap-3 px-6 py-5`}>
        <div className="flex items-baseline justify-between">
          <h2 className="text-base font-bold text-lms-ink">{t('Top courses by revenue')}</h2>
          <span className="text-[12.5px] text-lms-sub">{periodLabel}</span>
        </div>
        {report && report.top_courses.length > 0 ? (
          <ul className="flex flex-col divide-y divide-[var(--lms-line)]">
            {report.top_courses.map((c, i) => (
              <li key={c.course_id} className="flex items-center gap-3 py-2.5 text-sm">
                <span className="w-5 text-lms-faint lms-num">{i + 1}</span>
                <span className="min-w-0 flex-1 truncate text-lms-ink">{c.title || c.course_id}</span>
                <span className="font-bold text-lms-ink lms-num">{money(c.amount)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-sm text-lms-faint">{t('Nothing to rank yet — shows once the first sale comes in.')}</span>
        )}
      </section>

      {!data ? (
        <p className="text-sm text-lms-muted">{t('Loading…')}</p>
      ) : (
        <DataTable
          cols={COLS}
          empty={rows.length === 0 ? (
            <EmptyPanel bare icon={CreditCard} title={search ? t('No payments match your search.') : t('No payments yet')}>
              {!search && (
                <>
                  {t('Every purchase of a paid course lands here with its platform / instructor split.')}
                  {onSale.length > 0 && ` ${t('Paid courses on sale:')} ${onSale.length}.`}
                  {onSale.length > 0 && (
                    <span className="mt-4 flex flex-wrap justify-center gap-5">
                      {onSale.slice(0, 4).map((c) => (
                        <span key={c.id} className="flex items-center gap-2.5 text-left">
                          <Thumb src={c.thumbnail} title={c.title} />
                          <span className="flex flex-col">
                            <span className="text-sm text-lms-ink">{c.title}</span>
                            <span className="text-[12.5px] text-lms-sub lms-num">
                              {money(c.discount_flag ? c.discounted_price : c.price)}
                              {c.discount_flag ? ` · ${t('was')} ${money(c.price)}` : ''}
                            </span>
                          </span>
                        </span>
                      ))}
                    </span>
                  )}
                </>
              )}
            </EmptyPanel>
          ) : null}
        >
          {rows.map((p) => (
            <TableRow key={p.id} cols={COLS}>
              <PersonCell name={(p.member || {}).name} email={(p.member || {}).email} />
              <div className="flex min-w-0 items-center gap-3">
                <Thumb src={p.course_thumbnail} title={p.course_title} />
                <span className="truncate text-sm text-lms-ink">{p.course_title || '—'}</span>
              </div>
              <span className="text-[14.5px] font-bold text-lms-ink lms-num">{money(p.amount)}</span>
              <span className="text-[13.5px] text-lms-body lms-num">{money(p.admin_revenue)} / {money(p.instructor_revenue)}</span>
              <span className="text-[13px] text-lms-sub">{fmtDate(p.created_at)}</span>
            </TableRow>
          ))}
        </DataTable>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className={adminBtnOutline}>{t('Previous')}</button>
          <span className="text-sm text-lms-sub lms-num">{page} / {pages}</span>
          <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)} className={adminBtnOutline}>{t('Next')}</button>
        </div>
      )}
    </div>
  );
}
